import { ParentAttributeListUrlSortField, parentAttributeUrl } from "@dashboard/parentAttributes/urls";
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { getPrevLocationState } from "@dashboard/hooks/useBackLinkWithState";
import useNavigator from "@dashboard/hooks/useNavigator";
import { ListProps, SortPage } from "@dashboard/types";
import { Item } from "@glideapps/glide-data-grid";
import { Box } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import { parentAttributesListStaticColumnsAdapter, createGetCellContent } from "./datagrid";
import { messages } from "./messages";
import { ParentAttribute } from "../../types";

interface ParentAttributeListDatagridProps extends ListProps, SortPage<ParentAttributeListUrlSortField> {
  parentAttributes: ParentAttribute[];
  onSelectParentAttributeIds: (rowsIndex: number[], clearSelection: () => void) => void;
}

export const ParentAttributeListDatagrid = ({
  parentAttributes,
  settings,
  sort,
  disabled,
  onSort,
  onSelectParentAttributeIds,
  onUpdateListSettings,
}: ParentAttributeListDatagridProps) => {
  const datagridState = useDatagridChangeState();
  const location = useLocation();
  const navigate = useNavigator();
  const intl = useIntl();

  const parentAttributesListStaticColumns = useMemo(
    () => parentAttributesListStaticColumnsAdapter(intl, sort),
    [intl, sort],
  );

  const onColumnChange = useCallback(
    (picked: string[]) => {
      if (onUpdateListSettings) {
        onUpdateListSettings("columns", picked.filter(Boolean));
      }
    },
    [onUpdateListSettings],
  );

  const { handlers, visibleColumns, recentlyAddedColumn, staticColumns, selectedColumns } =
    useColumns({
      selectedColumns: settings?.columns ?? [],
      staticColumns: parentAttributesListStaticColumns,
      onSave: onColumnChange,
    });

  const getCellContent = useCallback(
    createGetCellContent({
      parentAttributes,
      columns: visibleColumns,
      intl,
    }),
    [parentAttributes, intl, visibleColumns],
  );

  const handleRowClick = useCallback(
    ([_, row]: Item) => {
      const rowData: ParentAttribute = parentAttributes[row];

      if (rowData) {
        navigate(parentAttributeUrl(rowData.id), {
          state: getPrevLocationState(location),
        });
      }
    },
    [parentAttributes],
  );

  const handleRowAnchor = useCallback(
    ([, row]: Item) => parentAttributeUrl(parentAttributes[row].id),
    [parentAttributes],
  );

  const handleHeaderClick = useCallback(
    (col: number) => {
      const columnName = visibleColumns[col].id as ParentAttributeListUrlSortField;

      onSort(columnName);
    },
    [visibleColumns, onSort],
  );

  return (
    <DatagridChangeStateContext.Provider value={datagridState}>
      <Datagrid
        readonly
        loading={disabled}
        rowMarkers="checkbox-visible"
        columnSelect="single"
        hasRowHover={true}
        onColumnMoved={handlers.onMove}
        onColumnResize={handlers.onResize}
        verticalBorder={false}
        rows={parentAttributes?.length ?? 0}
        availableColumns={visibleColumns}
        emptyText={intl.formatMessage(messages.empty)}
        onRowSelectionChange={onSelectParentAttributeIds}
        getCellContent={getCellContent}
        getCellError={() => false}
        selectionActions={() => null}
        menuItems={() => []}
        onRowClick={handleRowClick}
        onHeaderClicked={handleHeaderClick}
        rowAnchor={handleRowAnchor}
        recentlyAddedColumn={recentlyAddedColumn}
        renderColumnPicker={() => (
          <ColumnPicker
            staticColumns={staticColumns}
            selectedColumns={selectedColumns}
            onToggle={handlers.onToggle}
          />
        )}
        navigatorOpts={{ state: getPrevLocationState(location) }}
      />

      <Box paddingX={6}>
        <TablePaginationWithContext
          component="div"
          settings={settings}
          disabled={disabled}
          onUpdateListSettings={onUpdateListSettings}
        />
      </Box>
    </DatagridChangeStateContext.Provider>
  );
};